import React from 'react'
import { CheckCircle, AlertTriangle, Circle, ChevronRight, Image } from 'lucide-react'
import { cn } from '@/utils/cn'
import { RoomSection } from '@/types'
import { SectionStatus } from '@/utils/sectionStatus'

interface MobileOverviewCardProps {
  section: RoomSection
  status: SectionStatus
  imageCount: number
  detectionCount: number
  onTap: () => void
}

const STATUS_CONFIG: Record<SectionStatus, { color: string; bgColor: string; icon: React.ReactNode; label: string }> = {
  empty: {
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    icon: <Circle className="h-5 w-5" />,
    label: 'No images',
  },
  mismatch: {
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    icon: <AlertTriangle className="h-5 w-5" />,
    label: 'Needs attention',
  },
  complete: {
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    icon: <CheckCircle className="h-5 w-5" />,
    label: 'Complete',
  },
}

const MobileOverviewCard: React.FC<MobileOverviewCardProps> = ({
  section,
  status,
  imageCount,
  detectionCount,
  onTap,
}) => {
  const config = STATUS_CONFIG[status]

  return (
    <button
      onClick={onTap}
      className={cn(
        'w-full flex items-center gap-4 p-4 rounded-xl border',
        'bg-white active:bg-gray-50 transition-colors text-left',
        status === 'empty' && 'border-red-200',
        status === 'mismatch' && 'border-amber-200',
        status === 'complete' && 'border-emerald-200'
      )}
    >
      {/* Status Icon */}
      <div
        className={cn(
          'w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0',
          config.bgColor,
          config.color
        )}
      >
        {config.icon}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-gray-900 truncate">{section.name}</h3>
        <div className="flex items-center gap-3 mt-1">
          <span className={cn('text-sm font-medium', config.color)}>
            {config.label}
          </span>
          {imageCount > 0 && (
            <span className="text-sm text-gray-500 flex items-center gap-1">
              <Image className="h-3.5 w-3.5" />
              {imageCount}
            </span>
          )}
          {detectionCount > 0 && (
            <span className="text-sm text-gray-500">
              {detectionCount} items
            </span>
          )}
        </div>
      </div>

      {/* Chevron */}
      <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
    </button>
  )
}

export default MobileOverviewCard
